import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '@env';
import { LogSeverity, LoggingService } from '@core/services/logging.service';
import moment from 'moment-timezone';
import { LocationInformation } from '@shared/model';

export interface LocationServiceResponse {
  data: any[];
  serviceStatus: string;
  unit: string;
}

interface LocationCacheEntry {
  storeId: string;
  timestamp: number;
  data: LocationInformation;
}

@Injectable({
  providedIn: 'root',
})
export class LocationService {
  private static readonly CACHE_KEY = 'locationServiceCache';
  private static readonly CACHE_RESET_HOUR = 4; // cache expires at 4am store-local each day

  private currentDaypartVariation: LocationInformation = {};

  constructor(private http: HttpClient, private logger: LoggingService) {}

  async getStoreInformation(): Promise<LocationInformation> {
    await this.fetchStoreBreakfastTime();
    return this.currentDaypartVariation;
  }

  private async fetchStoreBreakfastTime() {
    if (!environment.locationServicesUrl) {
      // eslint-disable-next-line no-console
      console.error('LocationApi URL is missing!');
      return;
    }

    const storeId = localStorage.getItem('storeId');

    const cached = this.readCache(storeId);
    if (cached) {
      this.currentDaypartVariation = cached;
      return;
    }

    try {
      const response = await this.http
        .get(
          `${environment.locationServicesUrl}/LocationServices/rest/nearbyLocations?lang=en&cntry=US&sourceCode=ORDER.WENDYS&version=0&hasMobileOrder=true&includePOS=true&includePendingNew=true&siteNum=` +
            storeId,
        )
        .toPromise();
      const parsedData: LocationServiceResponse = (await response) as LocationServiceResponse;
      this.currentDaypartVariation = parsedData.data[0] as LocationInformation;
      this.writeCache(storeId, this.currentDaypartVariation);
    } catch (error) {
      await this.logger.writeLog('[LocationService] Error fetching location api:', {
        error: error,
        severity: LogSeverity.ERROR,
      });
    }
  }

  private readCache(storeId: string | null): LocationInformation | undefined {
    if (!storeId) {
      return undefined;
    }
    try {
      const raw = localStorage.getItem(LocationService.CACHE_KEY);
      if (!raw) {
        return undefined;
      }
      const entry = JSON.parse(raw) as LocationCacheEntry;
      if (entry.storeId !== storeId || !this.isFresh(entry)) {
        return undefined;
      }
      return entry.data;
    } catch {
      return undefined;
    }
  }

  // Cache is fresh until the next 1am in the store's local timezone.
  private isFresh(entry: LocationCacheEntry): boolean {
    const timezoneId = entry.data?.timezoneId;
    if (!timezoneId) {
      return false;
    }
    const now = moment.tz(timezoneId);
    const lastReset = now.clone().startOf('day').add(LocationService.CACHE_RESET_HOUR, 'hours');
    if (now.isBefore(lastReset)) {
      lastReset.subtract(1, 'day');
    }
    return entry.timestamp >= lastReset.valueOf();
  }

  private writeCache(storeId: string | null, data: LocationInformation) {
    if (!storeId) {
      return;
    }
    try {
      const entry: LocationCacheEntry = { storeId, timestamp: Date.now(), data };
      localStorage.setItem(LocationService.CACHE_KEY, JSON.stringify(entry));
    } catch {
      // ignore storage quota / serialization errors — cache is best-effort
    }
  }

  async storeCurrentTime() {
    const currentTime = moment();
    const menuTime = await this.checkStore(currentTime);
    return menuTime;
  }

  async checkStore(currentTime: moment.Moment) {
    try {
      await this.fetchStoreBreakfastTime();

      const beginBreakfast = moment(this.currentDaypartVariation.breakfastOpenTime, 'hh:mm:ss a');
      const endBreakfast = moment(this.currentDaypartVariation.breakfastCloseTime, 'hh:mm:ss a');
      const isBreakfast = currentTime.isBetween(beginBreakfast, endBreakfast);

      // await this.logger.writeLog(`[LocationService] Is breakfast time: ${isBreakfast}`, {
      //   beginBreakfast: beginBreakfast.format(),
      //   endBreakfast: endBreakfast.format(),
      //   severity: LogSeverity.INFO,
      // })

      if (isBreakfast) {
        return {
          menu: 'breakfast',
          endTime: endBreakfast,
          success: true,
        };
      } else {
        return {
          menu: 'full-menu',
          endTime: currentTime.add(1, 'day'),
          success: true,
        };
      }
    } catch (_error) {
      return {
        menu: 'full-menu',
        endTime: currentTime,
        success: false,
      };
    }
  }
}
